import type { TransferData } from '../../constants'
import { cleanText, firstText, normalizeDate, normalizeDescriptionText, parseDescriptionFromHtml, readMeta } from '../../utils/parser'
import type { JsonObject } from '../../utils/jsonld'
import { readBookJsonLd, stringifyJsonLdValue } from '../../utils/jsonld'

function readDetailValue (labels: string[]): string {
  const lowerLabels = labels.map((l) => l.toLowerCase())

  // Strategy: find ANY element whose text content matches a label,
  // then find the corresponding value in a sibling .col-8 element.
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      'span, sapn, .col-4, .col-4 > *'
    )
  )

  for (const el of candidates) {
    const raw = cleanText(el.textContent || '')
    const label = raw.replace(/[:：]$/, '').toLowerCase()
    if (!lowerLabels.includes(label)) continue

    // The label element is inside a .col-4 (or IS the .col-4).
    // Navigate up to the .col-4, then find sibling .col-8.
    const col4 = el.closest<HTMLElement>('.col-4') || el
    const row = col4.parentElement
    if (!row) continue

    // Find .col-8 sibling within the same row
    const col8 = row.querySelector<HTMLElement>(':scope > .col-8')
    if (!col8) continue

    const value = cleanText(col8.textContent || '')
    if (value) return value
  }

  return ''
}

function parseTitle (bookJsonLd: JsonObject | null): string {
  return firstText([
    '.info-name h1',
    '.info-block h1',
    'h1.h4',
    'section.info-block h1'
  ]) || stringifyJsonLdValue(bookJsonLd?.name) || readMeta('og:title') || 'book'
}

function parseAuthor (bookJsonLd: JsonObject | null): string {
  // Pubu page structure: <span>作者</span> then <a>Author Name</a>
  const metaAuthor = document.querySelector<HTMLElement>('.info-name-metaAuthor')
  if (metaAuthor) {
    const spans = Array.from(metaAuthor.querySelectorAll<HTMLElement>('span'))
    for (const span of spans) {
      if (cleanText(span.textContent || '') === '作者') {
        const authorLink = span.nextElementSibling as HTMLElement | null
        const name = cleanText(authorLink?.textContent || '')
        if (name) return name
      }
    }
  }

  // Fallback: any a inside info-name-metaAuthor that comes after "作者"
  const authorLinks = Array.from(document.querySelectorAll<HTMLElement>('.info-name-metaAuthor a'))
  const authorTexts = authorLinks
    .map((el) => cleanText(el.textContent || ''))
    .filter(Boolean)
  if (authorTexts.length > 0) {
    // First link after "作者" span is the author
    return authorTexts[0]
  }

  return stringifyJsonLdValue(bookJsonLd?.author)
}

function parsePublisher (bookJsonLd: JsonObject | null): string {
  // Pubu page structure: <span>出版</span> then <a>Publisher Name</a>
  const metaAuthor = document.querySelector<HTMLElement>('.info-name-metaAuthor')
  if (metaAuthor) {
    const spans = Array.from(metaAuthor.querySelectorAll<HTMLElement>('span'))
    for (const span of spans) {
      if (cleanText(span.textContent || '') === '出版') {
        const pubLink = span.nextElementSibling as HTMLElement | null
        const name = cleanText(pubLink?.textContent || '')
        if (name) return name
      }
    }
  }

  // Fallback: meta description often starts with "出版：PublisherName，作者：..."
  const metaDesc = readMeta('description')
  const pubMatch = metaDesc.match(/出版[：:]\s*(.+?)[，,]/)
  if (pubMatch?.[1]) return cleanText(pubMatch[1])

  return stringifyJsonLdValue(bookJsonLd?.publisher)
}

function parseIsbn (bookJsonLd: JsonObject | null): string {
  const fromDetails = readDetailValue(['ISBN', 'isbn'])
  const fromJson = stringifyJsonLdValue(bookJsonLd?.isbn)
  const value = fromDetails || fromJson
  return value.match(/(?:97[89])?\d[\d-]{8,15}[\dX]/i)?.[0]?.replace(/[^0-9X]/gi, '') || ''
}

function parsePubDate (bookJsonLd: JsonObject | null): string {
  const fromDetails = readDetailValue([
    // Traditional Chinese labels
    '發行', '出版日期', '出版日', '發布日期',
    // Simplified Chinese labels (zh-cn locale)
    '发行', '出版日期', '出版日', '发布日期',
    // English labels (current Pubu UI)
    'Released', 'Release Date', 'Publication Date'
  ])
  if (fromDetails) return normalizeDate(fromDetails)

  return normalizeDate(stringifyJsonLdValue(bookJsonLd?.datePublished))
}

function parseLanguage (bookJsonLd: JsonObject | null): string {
  const fromDetails = readDetailValue([
    // Traditional Chinese
    '語言',
    // Simplified Chinese (actual Pubu UI in zh-cn locale)
    '语言',
    // English
    'Language'
  ])
  if (fromDetails) return fromDetails

  const fromJson = stringifyJsonLdValue(bookJsonLd?.inLanguage)
  if (fromJson) return fromJson

  // Pubu's html lang attribute
  const htmlLang = document.documentElement.lang
  if (htmlLang === 'zh-tw') return '繁體中文'

  return ''
}

function parsePages (): string {
  // Only capture page count. Skip word count (字數) since
  // StoryGraph expects a page count, not a word count.
  return readDetailValue([
    // Traditional Chinese labels
    '頁數', '頁',
    // Simplified Chinese labels (zh-cn locale)
    '页数', '页',
    // English labels (current Pubu UI)
    'Pages', 'Page count'
  ])
}

function parseDescription (bookJsonLd: JsonObject | null): string {
  // Primary: #info-content tab pane
  const contentEl = document.querySelector<HTMLElement>('#info-content .font-base')
  if (contentEl) {
    const desc = parseDescriptionFromHtml(contentEl.innerHTML)
    if (desc) return desc
  }

  // Fallback: meta description (often has publisher/author prefix)
  const metaDesc = readMeta('description', 'og:description')
  // Strip the "出版：XXX，作者：XXX，" prefix if present
  const cleaned = metaDesc.replace(/^出版[：:].+?[，,]\s*作者[：:].+?[，,]\s*/, '')
  return normalizeDescriptionText(cleaned || stringifyJsonLdValue(bookJsonLd?.description))
}

function parseCoverUrl (bookJsonLd: JsonObject | null): string {
  // Primary: the lazy-loaded image
  const img = document.querySelector<HTMLImageElement>('.cover-block img[data-src]')
  if (img?.dataset.src) return img.dataset.src

  // Fallback: og:image
  const ogImage = readMeta('og:image')
  if (ogImage) return ogImage

  // Fallback: JSON-LD
  const jsonImage = stringifyJsonLdValue(bookJsonLd?.image)
  if (jsonImage) return jsonImage

  return ''
}

export function parsePubuPage (): TransferData {
  const bookJsonLd = readBookJsonLd()

  const data: TransferData = {
    title: parseTitle(bookJsonLd),
    originalTitle: '',
    author: parseAuthor(bookJsonLd),
    language: parseLanguage(bookJsonLd),
    publisher: parsePublisher(bookJsonLd),
    isbn: parseIsbn(bookJsonLd),
    pages: parsePages(),
    pubDate: parsePubDate(bookJsonLd),
    format: 'digital',
    desc: parseDescription(bookJsonLd),
    rating: '',
    review: '',
    coverUrl: parseCoverUrl(bookJsonLd)
  }

  console.log('[StoryGraphEnhancer] Pubu parsed data:', JSON.stringify(data, null, 2))

  return data
}
